import { StateCreator } from 'zustand';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
  // Auto-dismiss delay in ms. `undefined` falls back to the 5s default.
  // `null` means "persist until explicitly updated or dismissed".
  duration?: number | null;
  // When true, render a spinner and never auto-dismiss (implies persistent).
  // Used by background operations (e.g. uploads) that resolve the same toast
  // in place via `updateToast`.
  loading?: boolean;
  // Determinate upload progress, 0..1. `undefined` = no bar / indeterminate
  // (the "Finalizing…" leg, where bytes are in but the server's S3 put is
  // still pending). Only meaningful while `loading` is true.
  progress?: number;
  // When true, the loading toast offers a Cancel action. Cleared the moment the
  // upload can no longer be honored (the "Finalizing…" leg) so the UI never
  // promises a cancel it can't deliver.
  cancellable?: boolean;
  // Optional deep-link action rendered as a ToastAction. Used by the
  // finished-before-cancel notice to point the clinician at the document that
  // landed despite their cancel.
  linkAction?: { label: string; altText: string; href: string };
}

const DEFAULT_TOAST_DURATION = 5000;

const isPersistent = (toast: Pick<Toast, 'loading' | 'duration'>): boolean =>
  toast.loading === true || toast.duration === null;

export interface ToastSlice {
  toasts: Toast[];
  // Returns the new toast's id so callers can resolve it later via updateToast.
  addToast: (toast: Omit<Toast, 'id'>) => string;
  // Merge a patch into an existing toast in place (no-op if the id is gone).
  // If the patched toast is no longer persistent, its auto-dismiss is scheduled.
  updateToast: (id: string, patch: Partial<Omit<Toast, 'id'>>) => void;
  removeToast: (id: string) => void;
  showSuccess: (title: string, description?: string) => void;
  showError: (title: string, description?: string) => void;
}

export const toastSlice: StateCreator<ToastSlice> = (set, get) => ({
  toasts: [],

  addToast: (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { ...toast, id };

    set(state => ({
      toasts: [...state.toasts, newToast],
    }));

    // Persistent toasts (loading / duration:null) are dismissed explicitly,
    // not on a timer.
    if (!isPersistent(newToast)) {
      const duration = newToast.duration ?? DEFAULT_TOAST_DURATION;
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id;
  },

  updateToast: (id: string, patch: Partial<Omit<Toast, 'id'>>) => {
    let found = false;
    set(state => {
      const toasts = state.toasts.map(toast => {
        if (toast.id !== id) return toast;
        found = true;
        return { ...toast, ...patch };
      });
      return found ? { toasts } : state;
    });

    if (!found) return;

    const updated = get().toasts.find(toast => toast.id === id);
    if (!updated || isPersistent(updated)) return;

    // The toast just transitioned to a terminal, non-persistent state
    // (e.g. loading → success). Schedule its dismissal now.
    const duration = updated.duration ?? DEFAULT_TOAST_DURATION;
    setTimeout(() => {
      get().removeToast(id);
    }, duration);
  },

  removeToast: (id: string) => {
    set(state => ({
      toasts: state.toasts.filter(toast => toast.id !== id),
    }));
  },

  showSuccess: (title: string, description?: string) => {
    get().addToast({ title, description, variant: 'default' });
  },

  showError: (title: string, description?: string) => {
    get().addToast({ title, description, variant: 'destructive' });
  },
});
